import { isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component, useState } from 'react';
import DatePicker from 'react-datepicker';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import {
  Button,
  Dropdown,
  Form,
  Header,
  Message,
  Modal,
  Popup,
  TextArea,
} from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';

// Import component
import AmsAlert from '../../utils/AmsAlert';
import AmsDateFormatters from '../../utils/AmsDateFormatters';
import AmsFormLabel from '../../utils/AmsFormLabel';
import AmsLookupDropdown from '../../utils/AmsLookupDropdown';
import AmsModal from '../../utils/AmsModal';
import AmsTable from '../../utils/AmsTable';

//Import actions
import {
  fetchExternalUsers,
  reportShareMultipleAction,
} from '../../actions/reportActions';
import { fetchAmsLookup } from '../../actions/lookupActions';
import { showModalNew } from '../../actions/modalActions';
import { MODAL_TYPES } from '../Shared/modals/ModalTypes';

class ReportItems extends Component {
  state = {
    loading: false,
    sending: false,
    reports: [],
    page: 1,
    limit: 25,
    sortName: 'dueDate',
    sortOrder: 'desc',
    filters: {},
    showModal: false,
    selectedReports: [],
    externalUserOptions: [],
    modalErrors: {},
    confirmationDialogData: {},
    showConfirmationDialog: false,
    nonSelectableReports: [],
    modalData: {
      message: '',
      shareExpirationDate: null,
      sharedExternal: [],
      sharedInternal: [],
      sharedInternalData: [],
    },
    modalDataNew: {
      assignedExternalUsers: [],
      sharedExternalUsers: [],
      sharedInternalUsers: [],
      errors: {},
    },
    dlhUserOptions: [],
  };

  constructor(props) {
    super(props);
    this.amsTableRef = React.createRef();
  }

  static getDerivedStateFromProps(nextProps, state) {
    return { ...state, ...nextProps };
  }

  componentDidMount() {
    this.props.fetchAmsLookup('multipleReportDlhUsers').then(response => {
      this.setState({
        ...this.state,
        dlhUserOptions: this.getDLHUsersToOptions(response),
      });
    });
    this.props.fetchAmsLookup('excludeDlhUsers');
  }

  componentDidUpdate(prevProps) {
    // Typical usage (don't forget to compare props):
    if (prevProps.externalUsers.length !== this.props.externalUsers.length) {
      if (this.state.filters?.year < 2025) {
        this.setState({
          ...this.state,
          externalUserOptions: this.getEmailToOptions(this.props.externalUsers),
        });
      } else {
        this.setState({
          ...this.state,
          externalUserOptions: [
            ...this.getEmailToOptions(this.props.externalUsers),
            ...this.state.dlhUserOptions.filter(x =>
              this.getExcludedDLHUsers().has(x.key.toLowerCase())
            ),
          ],
        });
      }
    }
  }

  dateFormatter = cell => {
    return moment(cell).format('MM-DD-YYYY');
  };

  fetchModalData = () => {
    if (this.props.externalUsers.length === 0) {
      const reviewToUse = this.state.selectedReports[0];
      this.props.fetchExternalUsers({
        group: 'OHS',
        reviewId: reviewToUse.reviewId,
        reviewType: reviewToUse.reviewType,
      });
    }
  };

  getData = () => {
    const { page, limit, filters, sortName, sortOrder } = this.state;
    let input = {
      filters,
      pageNum: page,
      pageSize: limit,
      orderBy: sortName,
      sortOrder,
    };
    this.setState({ ...this.state, loading: true });

    this.props
      .fetchReports(input)
      .then(() => {
        this.setState({ ...this.state, loading: false });
      })
      .catch(() => {
        this.setState({ ...this.state, loading: false });
      });
  };

  assigneeFormatter = (cell, row) => {
    const assignee = this.props.users?.users?.find(u => u.oid === cell) || {
      name: row.assigneeName || 'None',
    };

    const assignees = row.assignees || [];
    const validAssignees = assignees.filter(
      a => a.assignedTo && a.assignedTo.trim() !== ''
    );
    const hasMultipleAssignees =
      validAssignees.length > 1 &&
      ['OHS', 'OGC', 'RO'].includes(row.reportStage);

    const linkId = `assignee-link-${row.reviewId}`;
    const tooltipId = `tooltip-${row.reviewId}`;

    const handleClick = () => {
      this.lastFocusedLinkId = linkId;
      this.props.showModalNew(MODAL_TYPES.ACTIVITY, {
        reviewId: row.reviewId,
        activeItem: 'sharedLog',
        enableCache: false,
        onClose: () => {
          const el = document.getElementById(this.lastFocusedLinkId);
          if (el) el.focus();
        },
      });
    };

    const handleKeyDown = e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    };

    const content =
      'This report was shared with multiple users. Follow link to see more details.';

    if (hasMultipleAssignees) {
      return (
        <Popup
          id={tooltipId}
          trigger={
            <a
              id={linkId}
              role="button"
              tabIndex={0}
              aria-describedby={tooltipId}
              aria-label={`${assignee.name}: ${content}`}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              className="assignee-link"
            >
              {assignee.name}
            </a>
          }
          position="top center"
          on={['hover', 'focus', 'click']}
        >
          <Popup.Content>{content}</Popup.Content>
        </Popup>
      );
    }

    return <span>{assignee.name}</span>;
  };

  teamStatusFormatter = (cell, row) => {
    return (
      <AmsLookupDropdown
        value={row.reportStage}
        fluid
        onChange={(e, { value }) => {
          this.props.actions.teamStatusUpdate(value, cell, row);
        }}
        selection
        category={'reportStages'}
      />
    );
  };

  linkId = cell => {
    return <Link href={`/reports/${cell}`}>{cell}</Link>;
  };

  findReportInSelectedReports = report => {
    const { selectedReports } = this.state;
    return selectedReports.findIndex(el => el.reviewId === report.reviewId);
  };

  selectReport = (row, isSelect) => {
    const { selectedReports } = this.state;

    if (isSelect) {
      if (this.canReportBeShared(row)) selectedReports.push(row);
    } else {
      selectedReports.splice(this.findReportInSelectedReports(row), 1);
    }
    this.setState({ ...this.state, selectedReports: selectedReports });
  };

  selectReports = (isSelect, rows) => {
    let rowsSelected = [];
    let selectedRows = [];
    if (isSelect) {
      for (let k = 0; k < rows.length; k++) {
        if (this.canReportBeShared(rows[k])) {
          rowsSelected.push(rows[k].reviewId);
          selectedRows.push(rows[k]);
        }
      }

      this.setState({
        ...this.state,
        selectedReports: selectedRows,
      });
    } else {
      this.setState({
        ...this.state,
        selectedReports: [],
      });
    }
    return rowsSelected;
  };

  getExcludedDLHUsers() {
    return new Set(
      (this.props.amsLookups['excludeDlhUsers'] || [])
        .filter(item => !item.parent && item.value?.value?.email)
        .map(item => item.value.value.email.toLowerCase())
    );
  }

  getEmailToOptions(input) {
    const toReturn = input.map(item => {
      return {
        text: item.firstName + ' ' + item.lastName + ', ' + item.group,
        value: { email: item.email, oid: item.oid },
        key: item.email,
      };
    });

    return toReturn;
  }

  getDLHUsersToOptions(input) {
    const toReturn = input.lookups
      .filter(item => item.value?.value && !item.parent && item.active)
      .map(item => {
        return {
          text: item.value.label,
          value: {
            email: item?.value?.value.email,
            oid: item?.value?.value?.oid,
          },
          key: item?.value?.value.email,
        };
      });
    return toReturn;
  }

  canReportBeShared = report => {
    return !(
      report.reviewStatus === 'Shipped' ||
      report.reviewStatus === 'Report Signed' ||
      report.reviewStatus === 'signed' ||
      report.reviewStatus === 'finalized' ||
      report.reportStage === 'Shipped' ||
      report.reportStage === 'Report Signed' ||
      report.reportStage === 'signed' ||
      report.reportStage === 'finalized' ||
      report.reportFinalized
    );
  };

  renderReportsTableNext = () => {
    const { limit } = this.state;

    const total = this.props.total;

    const classReviewTypes = [
      'CLASS',
      'AIAN-CLASS Reeval',
      'PR-CLASS',
      'VP-CLASS',
      'AIAN CLASS Self-Observations',
      'CLASS-Video',
      'CLASS AIAN Onsite',
      'CLASS AIAN Video',
    ];

    let reports = [];

    const dateFormatter = (cell, row) => {
      const dueDate = AmsDateFormatters.formatDate(cell);
      if ('12/31/1969' === dueDate) {
        return 'N/A';
      }
      return dueDate;
    };

    const daysLeftFormatter = (cell, row) => {
      if (null === row?.daysLeftToDueDate) {
        return 'N/A';
      }
      return cell;
    };

    const daysAtLocationFormatter = (cell, row) => {
      if (
        (null === row?.daysAtLocation &&
          null != row?.location &&
          !classReviewTypes.includes(row?.reviewType)) ||
        (null === row?.daysAtLocation &&
          classReviewTypes.includes(row?.reviewType))
      ) {
        return 'N/A';
      }
      return cell;
    };

    const locationFormatter = (cell, row) => {
      return cell;
    };

    const linkId = (cell, row) => {
      return (
        <Link
          to={{
            pathname: `/reports/${cell}`,
            state: { reviewType: `${row.reviewType}` },
          }}
        >
          {cell}
        </Link>
      );
    };

    if (this.props.reports) {
      reports = this.props.reports.map(item => ({
        ...item,
        granteeId: item.granteeIdList
          ? ((item.granteeIdList[0] || {}).granteeId || '').trim()
          : '',
        granteeName: item.granteeIdList
          ? ((item.granteeIdList[0] || {}).granteeName || '').trim()
          : '',
      }));
    }
    const selectRowProp = {
      mode: 'checkbox',
      clickToSelect: false,
      onSelect: this.selectReport,
      onSelectAll: this.selectReports,
      unselectable: this.state.nonSelectableReports,
      selected: this.state.selectedReports.map(e => e.reviewId),
      selectionHeaderRenderer: ({ mode, ...rest }) => {
        return (
          <>
            <span className="visually-hidden">Select All</span>
            <input
              type={mode}
              ref={input => {
                if (input) {
                  if (
                    this.state.selectedReports.length > 0 &&
                    this.state.selectedReports.length !== limit &&
                    this.state.selectedReports.length !==
                      this.state.reports.length
                  ) {
                    input.indeterminate = true;
                  }
                }
              }}
              title="Select All Reports on Page to Add to List of Multiple Report Sharing for OHS Director"
              {...rest}
            />
          </>
        );
      },
      selectionRenderer: ({ mode, ...rest }) => {
        rest.disabled = !this.canReportBeShared(
          this.props.reports[rest.rowIndex]
        );

        return (
          <input
            type={mode}
            title="Select Report to Add to List of Multiple Report Sharing for OHS Director"
            {...rest}
          />
        );
      },
    };

    const columns = [
      {
        dataField: 'reviewId',
        text: 'Review ID',
        formatter: linkId,
        sort: true,
      },
      {
        dataField: 'granteeId',
        text: 'Grantee ID',
        sort: true,
      },
      {
        dataField: 'granteeName',
        text: 'Grantee Name',
        sort: true,
      },
      {
        dataField: 'reviewType',
        text: 'Review Type',
        sort: true,
      },
      {
        dataField: 'dueDate',
        text: 'Due Date',
        formatter: dateFormatter,
        sort: true,
      },
      {
        dataField: 'assignee',
        text: 'Assignee',
        formatter: this.assigneeFormatter,
        sort: true,
      },
      {
        dataField: 'location',
        text: 'Location',
        formatter: locationFormatter,
        sort: true,
      },
      {
        dataField: 'daysAtLocation',
        text: 'Days at location',
        formatter: daysAtLocationFormatter,
        sort: true,
      },
      {
        dataField: 'daysLeftToDueDate',
        text: 'Work days left to due date',
        formatter: daysLeftFormatter,
        sort: true,
      },
    ];

    const scrollToRef = () => {
      window.scrollTo(0, this.amsTableRef.current.offsetTop);
      this.amsTableRef.current.focus();
    };

    const onSizePerPageChange = (sizePerPage, pageNum) => {
      this.setState(
        { ...this.state, page: pageNum, limit: sizePerPage },
        () => {
          this.getData();
          this.props.updatePageSize(sizePerPage);
          this.props.updatePageNum(pageNum);
          // Go to the top of the summary table
          scrollToRef();
        }
      );
    };

    const onPageChange = (pageNum, sizePerPage) => {
      this.setState(
        {
          ...this.state,
          page: pageNum,
          limit: sizePerPage,
        },
        () => {
          this.getData();
          this.props.updatePageSize(sizePerPage);
          this.props.updatePageNum(pageNum);

          // Go to the top of the summary table
          scrollToRef();
        }
      );
    };

    const onTableChange = (type, { sortField, sortOrder }) => {
      if (
        sortField === this.state.sortName &&
        sortOrder === this.state.sortOrder
      )
        return null;

      if (type === 'sort') {
        this.setState(
          {
            ...this.state,
            sortName: sortField || this.state.sortName,
            sortOrder: sortOrder || this.state.sortOrder,
          },
          () => {
            this.getData();
          }
        );
      }
    };

    const defaultSorted = [
      {
        dataField: this.state.sortName,
        order: this.state.sortOrder,
      },
    ];

    return (
      <div ref={this.amsTableRef} tabIndex="-1">
        <div className="all-findings-wrapper">
          <Button
            style={{ float: 'right' }}
            disabled={this.state.selectedReports.length === 0}
            onClick={() => {
              this.setState({ ...this.state, showModal: true });
              this.fetchModalData();
            }}
          >
            Share with OHS
          </Button>
        </div>
        <AmsTable
          defaultSorted={defaultSorted}
          page={this.state.page}
          total={total}
          selectRow={selectRowProp}
          limit={limit}
          loading={this.state.loading}
          keyField="reviewId"
          onTableChange={onTableChange}
          onPageChange={onPageChange}
          onSizePerPageChange={onSizePerPageChange}
          data={reports}
          columns={columns}
          customMessage={this.props.bubbleSelected}
        />
      </div>
    );
  };

  shareReportForm() {
    const { modalData } = this.state;
    return (
      <Form>
        <Form.Group widths="equal">
          <Form.Field>
            <AmsFormLabel
              name="Share with DLH"
              helpText={true}
              fieldLabel="shareWithDLH"
            >
              <AmsLookupDropdown
                control={AmsLookupDropdown}
                category={'multipleReportDlhUsers'}
                placeholder="Share with DLH"
                value={this.state.modalData.sharedInternal}
                onChange={(e, data) => {
                  //setSharedInternalUsers(value);

                  const sharedInternalData = data.options.filter(option => {
                    return data.value.includes(option.value);
                  });

                  this.setState({
                    ...this.state,
                    modalData: {
                      ...this.state.modalData,
                      sharedInternal: data.value,
                      sharedInternalData: sharedInternalData,
                    },
                  });
                }}
                fluid
                multiple
                search
                selection
                // clearable
                id="shareWithDLH"
                aria-labelledby="shareWithDLH"
                searchInput={{
                  id: 'shareWithDLHSearchInput',
                  title: 'search share with DLH',
                  'aria-labelledby': 'shareWithDLHSearchInput',
                }}
                openOnFocus={false}
              />
            </AmsFormLabel>
          </Form.Field>
          <Form.Field required>
            <AmsFormLabel
              name={`Share with OHS`}
              helpText={true}
              fieldLabel={`shareWithOHS`}
            >
              <Dropdown
                placeholder={`Share with OHS`}
                options={this.state.externalUserOptions}
                value={this.state.modalData.sharedExternal}
                onChange={(e, { value }) => {
                  this.setState({
                    ...this.state,
                    modalData: {
                      ...this.state.modalData,
                      sharedExternal: value,
                    },
                  });
                }}
                fluid
                multiple
                search
                selection
                // clearable
                id={`shareWithOHS`}
                aria-labelledby={`shareWithOHS`}
                searchInput={{
                  id: `shareWithOHSSearchInput`,
                  title: `search share with OHS`,
                  'aria-labelledby': `shareWithOHSSearchInput`,
                }}
                openOnFocus={false}
              />
            </AmsFormLabel>
          </Form.Field>
        </Form.Group>
        <Form.Field
          required
          selectsStart
          control={DatePicker}
          dateFormat="YYYY-MM-DD"
          placeholderText="Select Expiration Date"
          selected={
            modalData.shareExpirationDate &&
            AmsDateFormatters.getMoment(modalData.shareExpirationDate)
          }
          fromDate={new AmsDateFormatters.getMoment()}
          onChange={date => {
            let newDate = date
              ? AmsDateFormatters.getMoment(date).format('L')
              : null;

            this.setState({
              ...this.state,
              modalData: {
                ...modalData,
                shareExpirationDate: newDate,
              },
            });
          }}
          minDate={new AmsDateFormatters.getMoment()}
          value={
            modalData.shareExpirationDate ? modalData.shareExpirationDate : ''
          }
          popperPlacement="top-start"
          id="expirationDate"
          aria-labelledby="expirationDate"
          label={{
            children: 'Expiration Date',
            htmlFor: 'expirationDate',
          }}
        ></Form.Field>
        <Form.Field
          control={TextArea}
          rows="6"
          placeholder="your message ..."
          value={this.entityToChar(modalData.message, 'textarea')}
          onChange={e => {
            this.setState({
              ...this.state,
              modalData: {
                ...modalData,
                message: e.target.value,
              },
            });
          }}
          id="message"
          aria-labelledby="message"
          label={{
            children: 'Message',
            htmlFor: 'message',
          }}
        ></Form.Field>
      </Form>
    );
  }

  shareWithOHSReportForm() {
    const {
      modalData,
      modalDataNew,
      externalUserOptions = [],
      dlhUserOptions = [],
    } = this.state;
    const assignedExternalUsers = modalDataNew.assignedExternalUsers || [];
    const sharedExternalUsers = modalDataNew.sharedExternalUsers || [];
    const sharedInternalUsers = modalData.sharedInternal || [];

    const allToEmails = new Set(assignedExternalUsers.map(u => u.email));
    const allCcEmails = new Set([
      ...sharedExternalUsers.map(u => u.email),
      ...sharedInternalUsers.map(u => u.email),
    ]);

    const excludedEmails = this.getExcludedDLHUsers();

    const dlhEmails = dlhUserOptions
      .filter(x => !excludedEmails.has(x.key.toLowerCase()))
      .map(u => u.value?.email);
    const dlhEmailSet = new Set(dlhEmails);

    const ohsOnlyExternalUsers = externalUserOptions.filter(
      u => !dlhEmailSet.has(u.value?.email)
    );

    const ohsUsersForTo = ohsOnlyExternalUsers.filter(
      u => !allCcEmails.has(u.value?.email)
    );

    const ohsUsersForCC = ohsOnlyExternalUsers.filter(
      u => !allToEmails.has(u.value?.email)
    );

    const ccCombinedOptions = [
      ...ohsUsersForCC.map(u => ({
        key: u.value.email,
        value: u.value,
        text: u.text,
      })),
      ...dlhUserOptions
        .filter(u => !excludedEmails.has(u.key.toLowerCase()))
        .map(u => ({
          key: u.value.email,
          value: u.value,
          text: u.text,
        })),
    ];

    const ccCombinedValue = [...sharedExternalUsers, ...sharedInternalUsers];

    return (
      <Form>
        <Form.Field required>
          <AmsFormLabel
            name="To:"
            helpText
            fieldLabel={`shareWith${this.props.shareType}`}
          >
            <Dropdown
              placeholder="Share with OHS"
              value={assignedExternalUsers}
              onChange={(e, { value }) => {
                const selectedEmails = value.map(v => v.email);
                const sharedExternalFiltered = sharedExternalUsers.filter(
                  user => !selectedEmails.includes(user.email)
                );
                const sharedInternalFiltered = sharedInternalUsers.filter(
                  user => !selectedEmails.includes(user.email)
                );
                this.setState({
                  ...this.state,
                  modalData: {
                    ...modalData,
                    sharedInternal: sharedInternalFiltered,
                  },
                  modalDataNew: {
                    ...modalDataNew,
                    assignedExternalUsers: value,
                    sharedExternalUsers: sharedExternalFiltered,
                  },
                });
              }}
              options={ohsUsersForTo.map(u => ({
                key: u.value.email,
                value: u.value,
                text: u.text,
              }))}
              fluid
              multiple
              search
              selection
              error={modalDataNew.errors?.assignedExternalUsers}
              id={`shareWith${this.props.shareType}`}
              aria-labelledby={`shareWith${this.props.shareType}`}
              openOnFocus={false}
              searchInput={{
                id: `shareWith${this.props.shareType}SearchInput`,
                title: `search share with ${this.props.shareType}`,
                'aria-labelledby': `shareWith${this.props.shareType}SearchInput`,
              }}
            />
            {modalDataNew.errors?.assignedExternalUsers && (
              <div className="ui pointing above prompt label">
                {modalDataNew.errors.assignedExternalUsers}
              </div>
            )}
          </AmsFormLabel>
        </Form.Field>
        <Form.Field>
          <AmsFormLabel
            name="Copy Users:"
            helpText
            fieldLabel="copyAndDLHUsers"
          >
            <Dropdown
              placeholder="CC: Remaining OHS + DLH Users"
              value={ccCombinedValue}
              onChange={(e, { value }) => {
                const dlhSet = new Set(dlhEmails);
                const sharedInternal = value.filter(v => dlhSet.has(v.email));
                const sharedExternal = value.filter(v => !dlhSet.has(v.email));

                this.setState({
                  ...this.state,
                  modalData: {
                    ...modalData,
                    sharedInternal,
                    sharedInternalData: sharedInternal,
                  },
                  modalDataNew: {
                    ...modalDataNew,
                    sharedExternalUsers: sharedExternal,
                  },
                });
              }}
              options={ccCombinedOptions.map(u => ({
                key: u.value.email,
                value: u.value,
                text: u.text,
              }))}
              fluid
              multiple
              search
              selection
              id="ccUsers"
              aria-labelledby="ccUsers"
              searchInput={{
                id: 'ccUsersSearchInput',
                title: 'search cc users',
                'aria-labelledby': 'ccUsersSearchInput',
              }}
              openOnFocus={false}
            />
          </AmsFormLabel>
        </Form.Field>
        <Form.Field
          required
          control={DatePicker}
          dateFormat="YYYY-MM-DD"
          placeholderText="Select Expiration Date"
          selected={
            modalData.shareExpirationDate &&
            AmsDateFormatters.getMoment(modalData.shareExpirationDate)
          }
          fromDate={new AmsDateFormatters.getMoment()}
          onChange={date => {
            const newDate = date
              ? AmsDateFormatters.getMoment(date).format('L')
              : null;

            this.setState({
              ...this.state,
              modalData: {
                ...modalData,
                shareExpirationDate: newDate,
              },
            });
          }}
          minDate={new AmsDateFormatters.getMoment()}
          value={modalData.shareExpirationDate || ''}
          popperPlacement="top-start"
          id="expirationDate"
          aria-labelledby="expirationDate"
          label={{
            children: 'Expiration Date',
            htmlFor: 'expirationDate',
          }}
        />
        <Form.Field
          control={TextArea}
          rows="6"
          placeholder="your message ..."
          value={this.entityToChar(modalData.message, 'textarea')}
          onChange={e => {
            this.setState({
              ...this.state,
              modalData: {
                ...modalData,
                message: e.target.value,
              },
            });
          }}
          id="message"
          aria-labelledby="message"
          label={{ children: 'Message', htmlFor: 'message' }}
        />
      </Form>
    );
  }

  entityToChar(str, elm) {
    const el = document.createElement(elm);
    el.innerHTML = str;
    return el.value;
  }

  renderConfirmationDialog = () => {
    const { title, message } = this.state.confirmationDialogData;
    return (
      <AmsAlert
        show={this.state.showConfirmationDialog}
        title={title}
        type="success"
        showConfirm
        text={message}
        onConfirm={() => {
          this.setState({
            ...this.state,
            confirmationDialogData: {},
            showConfirmationDialog: false,
          });
        }}
      />
    );
  };

  generateModalAlert = () => {
    if (!isEmpty(this.state.modalErrors)) {
      return (
        <Message
          negative
          icon={'cancel'}
          header={'Something went wrong!'}
          content={`${this.state.modalErrors}`}
        ></Message>
      );
    }
  };

  renderShareModal() {
    const { modalData } = this.state;
    const { modalDataNew } = this.state;
    return (
      <AmsModal
        size="medium"
        closeOnDimmerClick={false}
        open={this.state.showModal}
        className="ams-semantic-modal-fix"
      >
        <Header as="h2" content="Share with OHS" />
        {this.generateModalAlert()}
        <Modal.Content>
          <Modal.Description>
            {this.state.filters?.year < 2025
              ? this.shareReportForm()
              : this.shareWithOHSReportForm()}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              this.setState({
                ...this.state,
                showModal: false,
                modalErrors: {},
                modalData: {
                  message: '',
                  shareExpirationDate: null,
                  sharedExternal: [],
                  sharedInternal: [],
                  sharedInternalData: [],
                },
              });
            }}
            content="Close"
          />
          <Button
            onClick={() => {
              this.submitShareForm();
            }}
            primary
            disabled={
              !modalData.shareExpirationDate ||
              (this.state.filters?.year < 2025 &&
                isEmpty(modalData.sharedExternal)) ||
              (this.state.filters?.year > 2024 &&
                isEmpty(modalDataNew.assignedExternalUsers)) ||
              this.state.sending
            }
            content="Send"
          />
        </Modal.Actions>
      </AmsModal>
    );
  }

  submitShareForm() {
    this.setState({ ...this.state, modalErrors: {}, sending: true });
    const { modalData } = this.state;
    const { modalDataNew } = this.state;
    const reviewList = this.state.selectedReports.map(review => {
      return {
        reviewID: review.reviewId,
        reportID: review.reportId,
        groupId: `group-${uuidv4()}`,
      };
    });
    const sharedInternalData = modalData.sharedInternalData.map(e => {
      return e.value;
    });
    let input = {
      sharedGroup: 'OHS',
      sharedExternal:
        this.state.filters?.year < 2025
          ? modalData.sharedExternal
          : modalDataNew.assignedExternalUsers,
      sharedInternal:
        this.state.filters?.year < 2025
          ? sharedInternalData
          : modalData.sharedInternalData,
      sharedExternalCopied: this.state.modalDataNew.sharedExternalUsers,
      reviewList,
      message: modalData.message,
      sharedDate: new Date().toISOString(),
      expirationDate: modalData.shareExpirationDate,
      sharedBy: this.props.currentUser.email,
      trigger: 'OHSLeadership',
    };

    this.props.reportShareMultipleAction(input, result => {
      if (result instanceof Error) {
        this.setState({
          ...this.state,
          sending: false,
          modalErrors: `Error: ${result.response.data.message ||
            'unknown error'}`,
        });
      } else {
        const alertMsg =
          reviewList.length === 1
            ? 'The report has been shared.'
            : 'The reports have been shared.';
        this.getData();
        this.setState({
          ...this.state,
          sending: false,
          showConfirmationDialog: true,
          showModal: false,
          selectedReports: [],
          confirmationDialogData: {
            title: 'Success',
            message: alertMsg,
          },
          modalData: {
            message: '',
            shareExpirationDate: null,
            sharedExternal: [],
            sharedInternal: [],
            sharedInternalData: [],
          },
        });
      }
    });
  }

  render() {
    return (
      <>
        {/* {this.renderReportsTable()} */}
        {this.renderShareModal()}
        {this.renderConfirmationDialog()}
        {this.renderReportsTableNext()}
      </>
    );
  }
}

ReportItems.propTypes = {
  reports: PropTypes.array.isRequired,
  lookups: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
  return {
    externalUsers: (state.report || {}).externalUsers,
    currentUser: state.auth.user,
    amsLookups: state.lookups.amsLookups,
  };
};

export default connect(mapStateToProps, {
  reportShareMultipleAction,
  fetchExternalUsers,
  fetchAmsLookup,
  showModalNew,
})(ReportItems);
